import { Box, IconButton, Tooltip, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { MemreFlexBox, MemreText, MemreButton } from 'components/core';
import * as Color from 'core/colors';
import styled from 'styled-components';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useApiUsers } from './hooks/useApiUsers';
import { useCreateApiUser } from './hooks/useCreateApiUser';
import { useDeleteApiUser } from './hooks/useDeleteApiUser';
import { ApiUser } from './types';
import React, { useState } from 'react';

export const ApiKeysManagement: React.FC = () => {
  const user = useCurrentUser();
  const partnerId = user?.primaryPartner.id;
  const [isCreating, setIsCreating] = useState(false);
  const [newApiUserName, setNewApiUserName] = useState('');

  const { data: apiUsers, isLoading, error } = useApiUsers(partnerId);
  const [createApiUser, { isLoading: isCreatingApi }] = useCreateApiUser();
  const [deleteApiUser, { isLoading: isDeletingApi }] = useDeleteApiUser();

  const handleCreateApiUser = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newApiUserName.trim() || !partnerId) return;

    createApiUser({
      partnerId,
      payload: { name: newApiUserName.trim() },
    });
    setNewApiUserName('');
    setIsCreating(false);
  };

  const handleDeleteApiUser = async (apiUserId: number) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    deleteApiUser({ id: apiUserId });
  };

  if (isLoading) {
    return (
      <Box sx={{ padding: '20px' }}>
        <MemreText>Loading API keys...</MemreText>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: '20px' }}>
        <MemreText sx={{ color: 'error.main' }}>Error loading API keys. Please try again.</MemreText>
      </Box>
    );
  }

  return (
    <div>
      {isCreating ? (
        <form onSubmit={handleCreateApiUser}>
          <TextField
            label="API Key Name"
            margin="normal"
            value={newApiUserName}
            onChange={(e) => setNewApiUserName(e.target.value)}
            sx={{ mt: 2 }}
            fullWidth
          />
          <FormButtons>
            <MemreButton
              text={isCreatingApi ? 'Creating...' : 'Create API Key'}
              variant="contained"
              color="secondary"
              onClick={handleCreateApiUser}
              disabled={!newApiUserName.trim() || isCreatingApi}
              size="medium"
            />
            <MemreButton
              text="Cancel"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setIsCreating(false);
                setNewApiUserName('');
              }}
              size="medium"
            />
          </FormButtons>
        </form>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <MemreButton
            text="Create New API Key"
            variant="contained"
            color="secondary"
            onClick={() => setIsCreating(true)}
            iconName="plus"
            size="medium"
          />
        </div>
      )}

      <ApiKeysContainer>
        {apiUsers && apiUsers.length > 0 ? (
          <ApiKeysGrid>
            {apiUsers.map((apiUser: ApiUser) => (
              <ApiKeyCard key={apiUser.id}>
                <MemreFlexBox justify="space-between" align="center">
                  <MemreFlexBox direction="column" gap={1}>
                    <MemreText variant="h6">{apiUser.name}</MemreText>
                    <MemreFlexBox direction="column" gap={0.5}>
                      <MemreText variant="body2" sx={{ color: 'text.secondary' }}>
                        API Key: <ApiKeyCode>{apiUser.oauth2AccessToken.token}</ApiKeyCode>
                      </MemreText>
                      <MemreText variant="body2" sx={{ color: 'text.secondary' }}>
                        Created: {new Date(apiUser.createdAt).toLocaleDateString()}
                      </MemreText>
                    </MemreFlexBox>
                  </MemreFlexBox>
                  <Tooltip title="Delete API Key">
                    <IconButton onClick={() => handleDeleteApiUser(apiUser.id)} color="error" disabled={isDeletingApi}>
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                  </Tooltip>
                </MemreFlexBox>
              </ApiKeyCard>
            ))}
          </ApiKeysGrid>
        ) : (
          <EmptyState>
            <MemreText sx={{ color: 'text.secondary' }}>
              No API keys found. Create your first API key to get started.
            </MemreText>
          </EmptyState>
        )}
      </ApiKeysContainer>
    </div>
  );
};

const FormButtons = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 10px;
`;

const ApiKeysContainer = styled.div`
  margin-top: 20px;
`;

const ApiKeysGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ApiKeyCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  background-color: ${Color.white};
`;

const ApiKeyCode = styled.code`
  background-color: ${Color.veryLightGray};
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
`;
